import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Product, MagazineContent } from '../types';
import { Plus, Minus, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Star, Truck, Shield } from 'lucide-react';
import ProductCard from './ProductCard';
import { useCart } from '../context/CartContext';
import { ProductGalleryCarousel } from './ProductGalleryCarousel';
import { getWindowOpenAt, getWindowCloseAt, getWindowState, isArtifactClaimed, isArtifactWithdrawn, formatWindowClose } from '../utils/productWindows';
import { isApparelProduct } from '../utils/productLogic';

interface ProductDetailProps {
  product: Product;
  onRelatedClick?: (product: Product) => void;
}

// Day-granularity only — no seconds-ticking displays in the purchase area
const OpensLine = ({ targetDate }: { targetDate: string }) => {
  const days = Math.ceil((+new Date(targetDate) - Date.now()) / (1000 * 60 * 60 * 24));
  const text = days <= 0 ? 'Opens today' : days === 1 ? 'Opens in 1 day' : `Opens in ${days} days`;
  return (
    <span className="font-mono text-sm tracking-[0.2em] uppercase">{text}</span>
  );
};

const ProductDetail: React.FC<ProductDetailProps> = ({ product, onRelatedClick }) => {
  const { addToCart } = useCart();
  const [selectedImage, setSelectedImage] = useState(product.image);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | undefined>(undefined);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>('description');
  const [error, setError] = useState<string | null>(null);

  // Touch swipe state
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset end position
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // Minimum 50px swipe distance
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage();
    } else if (isRightSwipe) {
      handlePreviousImage();
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleNextImage = () => {
    const currentIndex = images.findIndex(img => img === selectedImage);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  };

  const handlePreviousImage = () => {
    const currentIndex = images.findIndex(img => img === selectedImage);
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setSelectedImage(images[prevIndex]);
  };

  const handleAddToCart = () => {
    // Require a choice whenever options are offered — apparel sizes or the
    // explicit purchase options on made-to-order tiers (e.g. deck sizes)
    if (sizes.length > 0 && !selectedSize) {
      setError(isApparelProduct(product) ? 'Please select a size' : 'Please select an option');
      return;
    }
    setError(null);
    // One of each exists — artifacts can only be claimed once
    addToCart(product, product.tier === 'artifact' ? 1 : quantity, selectedSize, selectedColor || undefined);
  };

  const images = React.useMemo(() => {
    // Apparel products: Use color-specific images
    if (isApparelProduct(product)) {
      const activeColor = product.colors?.find(c => c.name === selectedColor) || product.colors?.[0];
      if (activeColor) {
        const colorImages = [
          activeColor.image,
          activeColor.backImage,
          ...(activeColor.gallery || []),
          ...(product.gallery || [])
        ].filter(Boolean) as string[];
        return colorImages.length > 0 ? colorImages : [product.image];
      }
    }

    // Standard products: Use main image + hover + gallery
    const imgs = [product.image];
    if (product.hoverImage && product.hoverImage !== product.image) imgs.push(product.hoverImage);
    if (product.gallery) imgs.push(...product.gallery);
    return Array.from(new Set(imgs)).filter(Boolean);
  }, [product, selectedColor]);

  // Sync carousel index when thumbnail clicked
  React.useEffect(() => {
    const index = images.findIndex(img => img === selectedImage);
    if (index !== -1) {
      setCurrentSlideIndex(index);
    }
  }, [selectedImage, images]);

  // Update selectedImage when carousel slides
  const handleSlideChange = (index: number) => {
    if (index !== currentSlideIndex) {
      setCurrentSlideIndex(index);
      setSelectedImage(images[index]);
    }
  };

  // Reset carousel when color changes (apparel products)
  React.useEffect(() => {
    if (selectedColor && isApparelProduct(product)) {
      setCurrentSlideIndex(0);
      if (images.length > 0) {
        setSelectedImage(images[0]);
      }
    }
  }, [selectedColor, product, images]);

  // Default content if not provided
  const description = product.description || "Made to order. Finished by hand.";
  const details = product.details || [];
  
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  // Fetch Related Products (AI Suggestions based on Category)
  React.useEffect(() => {
    const fetchRelated = async () => {
      try {
        const { collection, getDocs } = await import('firebase/firestore');
        const { db } = await import('../utils/firebase');
        const { getRecommendations } = await import('../utils/recommendationEngine');
        
        // Fetch ALL products to run the AI engine against
        // (Optimization: In a huge store we'd fetch only a subset, but for <1000 items this is instant and allows global matching)
        const querySnapshot = await getDocs(collection(db, 'products'));
        const allProducts: Product[] = [];
        querySnapshot.forEach(doc => {
            allProducts.push({ id: doc.id, ...doc.data() } as Product);
        });

        // Run the AI Engine
        const recommended = getRecommendations(product, allProducts, 3);
        setRelatedProducts(recommended);
        
      } catch (err) {
        console.error("Error generating AI recommendations", err);
      }
    };

    if (product) {
      fetchRelated();
      // Initialize selectedColor for apparel
      if (product.colors && product.colors.length > 0 && !selectedColor) {
           setSelectedColor(product.colors[0].name);
           setSelectedImage(product.colors[0].image);
      }
    }
  }, [product]);

  // Featured In Lookbooks Logic
  const [featuredLookbooks, setFeaturedLookbooks] = useState<MagazineContent[]>([]);

  React.useEffect(() => {
    const fetchLookbooks = async () => {
       if (!product) return;
       try {
         const { collection, query, where, getDocs } = await import('firebase/firestore');
         const { db } = await import('../utils/firebase');
         
         // Fetch active lookbooks
         // Note: We fetch all visible lookbooks and filter client-side because filtering by 
         // nested array object properties (lookbookImages[].productIds) is complex/limited in Firestore
         const q = query(collection(db, 'magazine'), where('visible', '==', true));
         const snapshot = await getDocs(q);
         
         const matches: MagazineContent[] = [];
         snapshot.forEach(doc => {
            const data = { id: doc.id, ...doc.data() } as MagazineContent;
            
            // Check global productIds
            if (data.productIds && data.productIds.includes(product.id)) {
                matches.push(data);
                return;
            }
            
            // Check per-image productIds
            if (data.lookbookImages) {
                const hasProduct = data.lookbookImages.some(img => img.productIds && img.productIds.includes(product.id));
                if (hasProduct) {
                    matches.push(data);
                }
            }
         });
         
         setFeaturedLookbooks(matches);
       } catch (err) {
         console.error("Error fetching featured lookbooks:", err);
       }
    };
    
    fetchLookbooks();
  }, [product]);

  // Determine available sizes from product data (Prioritize explicit sizes from DB)
  const isApparel = isApparelProduct(product);
  const sizes = product.sizes && product.sizes.length > 0 
    ? product.sizes
    : (isApparel
        ? ['S', 'M', 'L', 'XL']
        : []); // No default sizes for decks anymore
        
  // CHECK VARIANT AVAILABILITY
  const getVariantStock = (color: string | null, size: string | undefined) => {
    if (!product.variants || !color || !size) return null;
    const variantId = `${color}-${size}`;
    const variant = product.variants.find(v => v.id === variantId);
    return variant ? variant.stock : null;
  };

  // Window state — the core mechanic for fragments and artifacts
  const windowState = getWindowState(product);
  const windowOpenAt = getWindowOpenAt(product);
  const windowCloseAt = getWindowCloseAt(product);
  const isTiered = product.tier === 'fragment' || product.tier === 'artifact';
  const artifactClaimed = isArtifactClaimed(product);
  const artifactWithdrawn = isArtifactWithdrawn(product);
  // "Artifact {n}" — falls back to the plain noun when no number is assigned
  const artifactLabel = product.fragmentNumber ? `Artifact ${product.fragmentNumber}` : 'This artifact';

  const isSoldOut = React.useMemo(() => {
    // Fragments and artifacts are made to order — no stock-based sold-out logic.
    // sold_out status remains meaningful for legacy and uniform edge cases.
    if (isTiered) return false;
    // 1. Specific Variant Check
    if (product.variants && selectedColor && selectedSize) {
        const stock = getVariantStock(selectedColor, selectedSize);
        return stock !== null && stock === 0;
    }
    // 2. Fallback to Total Stock
    return (product.stock !== undefined && product.stock === 0) || product.status === 'sold_out';
  }, [product, selectedColor, selectedSize, isTiered]);

  // Helper to check if a specific size is available for the CURRENT color
  // (sizes remain options, not stock, for made-to-order tiers)
  const isSizeAvailable = (size: string) => {
    if (isTiered) return true;
    if (!product.variants) return true; // No stock matrix — nothing to enforce
    // Fall back to the first color so stock is enforced even before selection
    const color = selectedColor || product.colors?.[0]?.name;
    if (!color) return true;
    const stock = getVariantStock(color, size);
    return stock === null || stock > 0;
  };

  return (
    <div className="pt-24 min-h-screen bg-white">
      <div className="container mx-auto px-4 md:px-8">
        
        {/* Breadcrumbs */}
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-xs font-heading font-bold tracking-widest uppercase text-gray-400 mb-8">
          <Link to="/" className="hover:text-black">Home</Link>
          <span>/</span>
          <Link to={`/shop`} className="hover:text-black">Shop</Link>
          <span>/</span>
          <span className="text-black">{product.title}</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
          
          {/* Left Column: Image Gallery. Claimable items show true color —
              the grayscale archive treatment applies only to what can no
              longer be sold (closed windows, claimed/withdrawn artifacts,
              legacy sold-out): a record, not a shop. */}
          <div className="lg:w-3/5">
            <div className={
              (windowState === 'closed' || artifactClaimed || artifactWithdrawn || isSoldOut)
                ? '[&_img]:grayscale [&_img]:transition-all [&_img]:duration-500 hover:[&_img]:grayscale-0'
                : ''
            }>
              <ProductGalleryCarousel
                images={images}
                selectedIndex={currentSlideIndex}
                onSlideChange={handleSlideChange}
                isSoldOut={isSoldOut}
              />
            </div>
            
            {/* Thumbnails */}
            {isApparelProduct(product) ? (
                 /* APPAREL: Front/Back Toggle */
                 /* APPAREL: Front/Back Toggle */
                 /* APPAREL: Gallery Grid (Front + Back + Details) */
                 (() => {
                    const activeColor = product.colors?.find(c => c.name === selectedColor) || product.colors?.[0];
                    if (!activeColor) return null;

                    const colorImages = [
                        activeColor.image,
                        activeColor.backImage,
                        ...(activeColor.gallery || []),
                        ...(product.gallery || [])
                    ].filter(Boolean) as string[];

                    if (colorImages.length === 0) return null;

                    return (
                        <div className="grid grid-cols-4 gap-4">
                            {colorImages.map((img, idx) => (
                                <button 
                                    key={idx}
                                    onClick={() => setSelectedImage(img)}
                                    className={`aspect-square bg-gray-100 overflow-hidden transition-all ${selectedImage === img ? 'ring-2 ring-black' : 'opacity-70 hover:opacity-100'}`}
                                >
                                    {img.includes('.mp4') ? (
                                        <video src={img} className="w-full h-full object-cover pointer-events-none" muted />
                                    ) : (
                                        <img
                                            src={img || '/assets/placeholder.svg'}
                                            alt={`View ${idx + 1}`}
                                            onError={(e) => {
                                                const t = e.currentTarget;
                                                if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                                            }}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    );
                 })()
            ) : (

                /* STANDARD GALLERY (Skateboards) */
                <div className="grid grid-cols-4 gap-4">
                {images.map((img, idx) => (
                    <button 
                    key={idx}
                    onClick={() => setSelectedImage(img)}
                    className={`aspect-square bg-gray-100 overflow-hidden transition-opacity ${selectedImage === img ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`}
                    >
                    <img
                        src={img || '/assets/placeholder.svg'}
                        alt={`View ${idx + 1}`}
                        onError={(e) => {
                            const t = e.currentTarget;
                            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                        }}
                        className="w-full h-full object-cover"
                    />
                    </button>
                ))}
                </div>
            )}
          </div>

          {/* Right Column: Product Info */}
          <div className="lg:w-2/5 flex flex-col h-full sticky top-24">
            
            {/* Archive register — provenance and the terms of the window */}
            {(product.provenanceLine || isTiered) && (
              <div className="mb-6 space-y-2 font-mono text-xs text-gray-500 tracking-wider leading-relaxed border-b border-gray-100 pb-6">
                {product.provenanceLine && <p>{product.provenanceLine}</p>}
                {product.tier === 'fragment' && (
                  <>
                    <p>The original is gone. This is its reissue, stamped and returned to circulation.</p>
                    {windowState === 'open' && windowCloseAt && (
                      <p>Open until {formatWindowClose(windowCloseAt)}. Then closed, permanently.</p>
                    )}
                    {windowState === 'closed' && <p>Closed. Held, not sold.</p>}
                    <p>The edition is sized by the window. Each piece is numbered in order of claim. Nothing is reprinted.</p>
                  </>
                )}
                {product.tier === 'artifact' && (
                  <>
                    <p>One exists. It will be made once, for whoever claims it.</p>
                    {artifactWithdrawn ? (
                      <p>{artifactLabel} has returned to the archive. It will not be offered again.</p>
                    ) : artifactClaimed ? (
                      <p>Claimed.</p>
                    ) : (
                      <p>Open for 72 hours. If unclaimed, it returns to the archive and will not be offered again.</p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="mb-2 text-brand-gold font-heading font-bold tracking-widest text-sm uppercase">
              {product.series}
            </div>

            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase leading-none mb-4">
              {product.title}
            </h1>

            <div className="flex items-center justify-between mb-8 pb-8 border-b border-gray-100">
               <span className="font-body text-2xl text-gray-900">£{product.price.toFixed(2)}</span>
{/* Reviews Removed */}
            </div>
            
            {/* Description - Moved after price */}
            <div className="mb-8">
              <p className="font-body text-gray-600 leading-relaxed text-sm">
                {description}
              </p>
            </div>

            {/* FEATURED IN LOOKBOOKS */}
            {featuredLookbooks.length > 0 && (
              <div className="mb-8 p-4 bg-gray-50 border border-gray-100 rounded-sm">
                 <h3 className="font-heading font-bold text-xs uppercase tracking-widest text-gray-500 mb-4">Featured In</h3>
                 <div className="flex flex-col gap-3">
                   {featuredLookbooks.map(lb => (
                     <Link key={lb.id} to={`/magazine/${lb.id}`} className="group flex items-center gap-4 hover:bg-white transition-colors p-2 rounded-sm">
                        <div className="w-16 h-12 bg-gray-200 overflow-hidden flex-shrink-0">
                           <img
                             src={lb.coverImage || '/assets/placeholder.svg'}
                             alt={lb.title}
                             onError={(e) => {
                               const t = e.currentTarget;
                               if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                             }}
                             className="w-full h-full object-cover"
                           />
                        </div>
                        <div>
                           <p className="font-heading font-bold text-xs uppercase group-hover:underline">{lb.title}</p>
                           {lb.season && <p className="text-[10px] text-gray-500 uppercase tracking-wider">{lb.season}</p>}
                        </div>
                     </Link>
                   ))}
                 </div>
              </div>
            )}
            
            {/* SEASON DISPLAY */}
            {product.launchSeason && (
                <div className="mb-4">
                    <span className="inline-block border border-gray-300 px-3 py-1 text-xs font-bold uppercase tracking-widest text-gray-500">
                        Season: {product.launchSeason}
                    </span>
                </div>
            )}

            {/* Selectors */}
            <div className="space-y-6 mb-8">
              
              {/* Color Selector */}
              {product.colors && product.colors.length > 0 && (
                <div>
                    <label className="font-heading font-bold text-xs uppercase tracking-widest mb-2 block">Available Colors</label>
                    <div className="flex gap-3">
                        {product.colors.map((color, idx) => (
                            <button
                                key={idx}
                                onClick={() => { 
                                    setSelectedColor(color.name); 
                                    setSelectedImage(color.image); 
                                }}
                                className={`w-10 h-10 rounded-full border-2 p-0.5 overflow-hidden transition-all ${selectedColor === color.name ? 'border-black scale-110' : 'border-transparent hover:border-gray-300'}`}
                                title={color.name}
                            >
                                <img
                                    src={color.image || '/assets/placeholder.svg'}
                                    alt={color.name}
                                    onError={(e) => {
                                        const t = e.currentTarget;
                                        if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
                                    }}
                                    className="w-full h-full object-cover rounded-full"
                                />
                            </button>
                        ))}
                    </div>
                    {selectedColor && <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{selectedColor}</p>}
                </div>
              )}

              {/* Size/Option Selector - Only show if there are sizes */}
              {sizes.length > 0 && (
              <div>
                <div className="flex justify-between mb-2">
                   <label className="font-heading font-bold text-xs uppercase tracking-widest">Select Option</label>
                   <button className="text-xs text-gray-500 underline">Size Guide</button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                   {sizes.map((size) => {
                     const available = isSizeAvailable(size);
                     return (
                     <button 
                       key={size} 
                       disabled={!available}
                       onClick={() => { setSelectedSize(size); setError(null); }}
                       className={`border py-3 font-heading text-sm transition-all relative overflow-hidden ${
                         !available ? 'cursor-not-allowed bg-gray-50 text-gray-300 border-gray-200' :
                         selectedSize === size 
                           ? 'border-black bg-black text-white' 
                           : 'border-gray-300 hover:border-black'
                       }`}
                     >
                       {size}
                       {!available && <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="absolute w-[120%] h-[1px] bg-red-500 rotate-45"></div>
                          <div className="absolute w-[120%] h-[1px] bg-red-500 -rotate-45"></div>
                       </span>}
                     </button>
                     );
                   })}
                </div>
                {error && <p className="text-red-500 text-xs mt-2 font-bold">{error}</p>}
              </div>
              )}

              {/* Quantity (artifacts are 1 of 1 — no quantity selection) */}
              {product.tier !== 'artifact' && (
                <div className="flex items-center border border-gray-300 w-32">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <div className="flex-1 text-center font-heading font-bold">{quantity}</div>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-gray-100 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Conditional Claim / Countdown / Closed / Sold Out */}
            {isSoldOut ? (
                <button
                  disabled
                  className="w-full bg-gray-200 text-gray-500 font-heading font-bold text-lg py-4 tracking-[0.2em] cursor-not-allowed uppercase mb-4"
                >
                  Sold Out
                </button>
            ) : artifactWithdrawn || artifactClaimed ? (
                <button
                  disabled
                  className="w-full bg-gray-200 text-gray-500 font-heading font-bold text-lg py-4 tracking-[0.2em] cursor-not-allowed uppercase mb-4"
                >
                  {artifactClaimed ? 'Claimed' : 'Withdrawn'}
                </button>
            ) : windowState === 'upcoming' && windowOpenAt ? (
              <div className="bg-black text-white p-6 mb-4 text-center">
                <OpensLine targetDate={windowOpenAt} />
              </div>
            ) : windowState === 'closed' ? (
                <button
                  disabled
                  className="w-full bg-gray-200 text-gray-500 font-heading font-bold text-lg py-4 tracking-[0.2em] cursor-not-allowed uppercase mb-4"
                >
                  Closed
                </button>
            ) : (
              <>
                {/* Non-tiered products still state the close date here as a plain fact */}
                {!isTiered && windowCloseAt && (
                  <p className="text-center text-sm text-gray-600 font-body mb-4">
                    Open until {formatWindowClose(windowCloseAt)}.
                  </p>
                )}
                {product.leadTimeText && (
                  <p className="text-center text-xs text-gray-500 font-body mb-4">
                    {product.leadTimeText}
                  </p>
                )}
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-brand-gold text-white font-heading font-bold text-lg py-4 tracking-[0.2em] hover:bg-black transition-colors uppercase mb-4"
                >
                  Claim
                </button>
              </>
            )}
            <p className="text-center text-xs text-gray-500 font-body mb-8">
              Free shipping on orders of £120 or more.
            </p>

            {/* Accordions */}
            <div className="border-t border-gray-200">
              


              {/* Details */}
              <div className="border-b border-gray-200">
                <button 
                  onClick={() => toggleSection('details')}
                  className="w-full flex justify-between items-center py-4 font-heading font-bold uppercase tracking-widest hover:text-brand-gold transition-colors"
                >
                  <span>Details & Specs</span>
                  {openSection === 'details' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                 <div className={`overflow-hidden transition-all duration-300 ${openSection === 'details' ? 'max-h-[500px] opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
                   {/* Specs */}
                   {product.specs && (
                     <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 mb-4 pb-4 border-b border-gray-100">
                       {Object.entries(product.specs).map(([key, value]) => (
                         <span key={key}>
                           <span className="text-gray-400">{key === 'L' ? 'Length' : key === 'W' ? 'Width' : key === 'WB' ? 'Wheelbase' : key}:</span> {value}
                         </span>
                       ))}
                     </div>
                   )}
                   <ul className="list-disc pl-5 space-y-1 font-body text-gray-600 text-sm">
                     {details.map((detail, idx) => (
                       <li key={idx}>{detail}</li>
                     ))}
                   </ul>
                </div>
              </div>

               {/* Shipping */}
               <div className="border-b border-gray-200">
                <button 
                  onClick={() => toggleSection('shipping')}
                  className="w-full flex justify-between items-center py-4 font-heading font-bold uppercase tracking-widest hover:text-brand-gold transition-colors"
                >
                  <span>Shipping & Returns</span>
                  {openSection === 'shipping' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                 <div className={`overflow-hidden transition-all duration-300 ${openSection === 'shipping' ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Truck className="text-brand-gold mt-1" size={20} />
                        <div>
                          <p className="font-heading font-bold text-xs uppercase">Shipping</p>
                          <p className="text-xs text-gray-500">Free on orders of £120 or more. Otherwise £5 UK, £15 international.</p>
                        </div>
                      </div>
                       <div className="flex items-start gap-3">
                        <Shield className="text-brand-gold mt-1" size={20} />
                        <div>
                          <p className="font-heading font-bold text-xs uppercase">Returns</p>
                          <p className="text-xs text-gray-500">
                            <Link to="/support?section=returns" className="underline hover:text-black">
                              See Returns &amp; Exchanges
                            </Link>{' '}
                            for the full policy.
                          </p>
                        </div>
                      </div>
                    </div>
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* Related Products */}
        <div className="mt-24 mb-12">
           <h3 className="font-heading font-bold text-2xl uppercase tracking-widest text-center mb-12">You Might Also Like</h3>
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedProducts.map(p => (
                <ProductCard key={p.id} product={p} onClick={onRelatedClick} />
              ))}
           </div>
        </div>

      </div>
    </div>
  );
};

export default ProductDetail;